import React, { FC } from "react";
import { AdCircuit, EditAdCircuit } from "../../../../types";
import { Button } from "../../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../Grid";
import { Input, Label, Textarea } from "../../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import { useStores } from "../../../../hooks/useStores";
import { observer } from "mobx-react";

type Props = {
  adCircuit?: AdCircuit;
  isSubmitting: boolean;
  onSubmit: (values: EditAdCircuit) => void;
  editable?: boolean;
  isAdmin?: boolean;
  validable: boolean;
  onCancel: () => void;
};

const AdvertisingCircuitForm: FC<Props> = observer(
  ({
    adCircuit,
    isSubmitting,
    onSubmit,
    editable = true,
    isAdmin = false,
    validable,
    onCancel,
  }) => {
    const { categoryStore } = useStores();
    const { t } = useTranslation();

    const formik = useFormik({
      initialValues: {
        id: adCircuit?.id || null,
        title: adCircuit?.title || "",
        description: adCircuit?.description || "",
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape({
        title: Yup.string().required(
          t("components.common.advertisingCircuitForm.titleRequired")
        ),
      }),
    });

    return (
      <form onSubmit={formik.handleSubmit}>
        <Container pb={"20px"}>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="title">
                  {t("components.common.advertisingCircuitForm.title")}
                </Label>
                <Input
                  id="title"
                  name="title"
                  placeholder={t(
                    "components.common.advertisingCircuitForm.titlePlaceholder"
                  )}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  state={formik.errors.title ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("title").error}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="description">
                  {t("components.common.advertisingCircuitForm.description")}
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder={t(
                    "components.common.advertisingCircuitForm.descriptionPlaceholder"
                  )}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  showState={true}
                />
              </FormControl>
            </Col>
          </Row>
        </Container>

        <Row>
          <Col textAlign="end">
            <Button
              type={"reset"}
              onClick={onCancel}
              marginRight="10px"
              color="light"
            >
              {t("common.cancel")}
            </Button>
            <Button type={"submit"}>{t("common.save")}</Button>
          </Col>
        </Row>
      </form>
    );
  }
);

export default AdvertisingCircuitForm;
